import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TeachersPage from "./pages/TeachersPage";
import StudentsPage from "./pages/StudentsPage";
import ClassesPage from "./pages/ClassesPage";
import ResultsPage from "./pages/ResultsPage";
import MyResultsPage from "./pages/MyResultsPage";
import UsersPage from "./pages/UsersPage";
import SettingsPage from "./pages/SettingsPage";
import ActivityLogsPage from "./pages/ActivityLogsPage";
import AdminResultsDashboard from "./pages/AdminResultsDashboard";
import ExportPage from "./pages/ExportPage";
import PrintResultsPage from "./pages/PrintResultsPage";
import SubjectsPage from "./pages/SubjectsPage";
import Layout from "./components/Layout";
import TripsPage from "./pages/TripsPage";
import TripDetailPage from "./pages/TripDetailPage";
import TripCreatePage from "./pages/TripCreatePage";
import TripPrintPage from "./pages/TripPrintPage";
import FeesManagementPage from "./pages/FeesManagementPage";
import MyFeesPage from "./pages/MyFeesPage";
import GroceryPage from "./pages/GroceryPage";
import GroceryStudentStatsPage from "./pages/GroceryStudentStatsPage";
import TermManagementPage from "./pages/TermManagementPage";
import MyGroceriesPage from "./pages/MyGroceriesPage";

// v1.1 Finance & Operations Pages
import AccountantDashboard from "./pages/AccountantDashboard";
import ExpensesPage from "./pages/ExpensesPage";
import TeacherSalariesPage from "./pages/TeacherSalariesPage";
import StatutoryChargesPage from "./pages/StatutoryChargesPage";
import FeesPaymentsPage from "./pages/FeesPaymentsPage";
import TripFinancePage from "./pages/TripFinancePage";
import GroceryCashPage from "./pages/GroceryCashPage";
import UniformManagementPage from "./pages/UniformManagementPage";
import MyUniformsPage from "./pages/MyUniformsPage";
import FinanceReportsPage from "./pages/FinanceReportsPage";

import "@/App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};


function AppRoutes() {
  const { user } = useAuth();

  const getDefaultRoute = (role) => {
    if (role === "accountant") {
      return "/accountant/dashboard";
    }

    return "/dashboard";
  };

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to={getDefaultRoute(user.role)} replace /> : <LoginPage />} 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/teachers" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TeachersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students" 
        element={
          <ProtectedRoute allowedRoles={["admin", "teacher"]}>
            <StudentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute allowedRoles={["admin", "teacher"]}>
            <ClassesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/results" 
        element={
          <ProtectedRoute allowedRoles={["admin", "teacher"]}>
            <ResultsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/results-analytics" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminResultsDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/my-results" 
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <MyResultsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/activity-logs" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ActivityLogsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/export" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ExportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/print-results" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <PrintResultsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subjects" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SubjectsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/fees" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <FeesManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/term-management" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TermManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/groceries" 
        element={
          <ProtectedRoute allowedRoles={["admin", "teacher"]}>
            <GroceryPage />
          </ProtectedRoute>
        } 
      />
      <Route
        path="/grocery-student-stats"
        element={
          <ProtectedRoute allowedRoles={["admin", "teacher"]}>
            <GroceryStudentStatsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/my-groceries" 
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <MyGroceriesPage />
          </ProtectedRoute>
        } 
      />
      {/* Trip Management routes */}
      <Route 
        path="/my-fees" 
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <MyFeesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/trips" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TripsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/trips/new" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TripCreatePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/trips/:tripId/print" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TripPrintPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/trips/:tripId" 
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TripDetailPage />
          </ProtectedRoute>
        } 
      />

      {/* v1.1 Accountant Routes */}
      <Route 
        path="/accountant/dashboard" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <AccountantDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/fees" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <FeesPaymentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/expenses" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <ExpensesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/salaries" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <TeacherSalariesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/statutory" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <StatutoryChargesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/grocery-cash" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <GroceryCashPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/uniforms" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <UniformManagementPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/trips" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <TripFinancePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/accountant/reports" 
        element={
          <ProtectedRoute allowedRoles={["admin", "accountant"]}>
            <FinanceReportsPage />
          </ProtectedRoute>
        } 
      />

      {/* Student Finance Routes */}
      <Route 
        path="/student/grocery-cash" 
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <GroceryCashPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student/uniforms" 
        element={
          <ProtectedRoute allowedRoles={["student"]}>
            <MyUniformsPage />
          </ProtectedRoute>
        } 
      />

      <Route path="/" element={<Navigate to={getDefaultRoute(user?.role)} replace />} />
      <Route path="*" element={<Navigate to={getDefaultRoute(user?.role)} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
